import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Typography,
  Space,
  message,
  InputNumber,
  Divider,
  Row,
  Col,
  Avatar,
  Upload,
} from 'antd';
import { SaveOutlined, UserOutlined, UploadOutlined } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ProfileConfig = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [avatarPreview, setAvatarPreview] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get('/profile/admin');
      if (response.data.success && response.data.data.profile) {
        const profile = response.data.data.profile;
        form.setFieldsValue({
          ...profile,
          winRate: parseFloat(profile.winRate) || 0,
          totalLikes: profile.totalLikes || 0
        });
        setAvatarPreview(profile.avatar || '');
      }
    } catch (error) {
      message.error('获取个人资料配置失败');
      console.error('Fetch profile error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();
      
      await api.put('/profile', values);
      message.success('个人资料配置已保存');
    } catch (error) {
      message.error('保存失败');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = (e) => {
    setAvatarPreview(e.target.value);
  };

  // 处理头像上传
  const handleAvatarUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/profile/upload-avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.data.avatarUrl) {
        const url = response.data.data.avatarUrl;
        setAvatarPreview(url);
        form.setFieldsValue({ avatar: url });
        message.success('头像上传成功');
        if (onSuccess) {
          onSuccess(response.data, file);
        }
      } else {
        const msg = response.data.message || '头像上传失败';
        message.error(msg);
        if (onError) {
          onError(new Error(msg));
        }
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      message.error('头像上传失败');
      if (onError) {
        onError(error);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>
          <UserOutlined style={{ marginRight: 8 }} />
          首页交易员文档配置
        </Title>
        <Button 
          type="primary" 
          icon={<SaveOutlined />} 
          onClick={handleSave}
          loading={saving}
        >
          保存配置
        </Button>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card title="个人资料" loading={loading}>
            <Form form={form} layout="vertical">
              <Divider>基本信息</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="name" label="英文名称" rules={[{ required: true }]}>
                    <Input placeholder="Gary Joe Bartak" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="nameCN" label="中文名称">
                    <Input placeholder="加里·乔·巴塔克" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="title" label="英文头衔" rules={[{ required: true }]}>
                    <Input placeholder="Chief Analyst" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="titleCN" label="中文头衔">
                    <Input placeholder="首席分析师" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="avatar" label="头像图片URL">
                <Input 
                  placeholder="https://example.com/avatar.jpg 或通过下方上传自动填写" 
                  onChange={handleAvatarChange}
                />
              </Form.Item>

              <Form.Item label="上传头像">
                <Upload
                  showUploadList={false}
                  customRequest={handleAvatarUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    {uploading ? '上传中...' : '点击上传头像图片'}
                  </Button>
                </Upload>
                <Text type="secondary">
                  上传成功后，会自动填充上方头像URL并更新右侧预览
                </Text>
              </Form.Item>

              <Form.Item name="verified" label="认证标识" valuePropName="checked">
                <Switch checkedChildren="已认证" unCheckedChildren="未认证" />
              </Form.Item>

              <Divider>统计数据</Divider>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="experience" label="工作年限" rules={[{ required: true }]}>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="totalTrades" label="总交易量" rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="winRate" label="胜率 (%)" rules={[{ required: true }]}>
                    <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="totalLikes" label="点赞数量" rules={[{ required: true }]}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>跟随按钮</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="followButtonText" label="按钮文字">
                    <Input placeholder="跟随" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="followLink" label="跳转链接">
                    <Input placeholder="/register 或 https://example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>Join Community 按钮</Divider>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="joinCommunityText" label="按钮文字">
                    <Input placeholder="Join Community" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="joinCommunityLink" label="跳转链接">
                    <Input placeholder="/register 或 https://example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider>其他</Divider>

              <Form.Item name="description" label="个人简介">
                <TextArea rows={3} placeholder="请输入个人简介..." />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="头像预览">
            <div style={{ textAlign: 'center' }}>
              <Avatar 
                size={120} 
                src={getImageUrl(avatarPreview)}
                icon={<UserOutlined />}
                style={{ marginBottom: 16 }}
              />
              <Text type="secondary" style={{ display: 'block' }}>
                建议使用正方形图片，推荐尺寸 200x200
              </Text>
            </div>
          </Card>

          <Card title="说明" style={{ marginTop: 16 }}>
            <Space direction="vertical">
              <Text type="secondary">
                此配置将显示在网站首页的个人资料卡片中
              </Text>
              <Text type="secondary">
                跟随链接可以是站内路径（如 /register）或完整URL
              </Text>
              <Text type="secondary">
                完整URL会在新标签页打开
              </Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default ProfileConfig;

